import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { parentService } from 'src/services/services/parent/parent.api';
import { chatService } from 'src/services/services/chat/chat.api';
import {
  setLevels,
  setSelectedLevel,
  setSubjects,
  setLoading,
  setError,
} from 'src/redux/features/chat/parentChatSlice';
import * as chatActions from 'src/redux/redux-store/chat/chatReducer';
import { ResponseKind } from 'src/services/services/MainService';
import { ChatSession } from 'src/interfaces/Chat';

// Subject type definition
interface Subject {
  id: string;
  name: string;
  code: string;
  is_required: boolean;
  levelId: string;
  level_id: string; // For backward compatibility
}

const useParentChat = () => {
  const dispatch = useAppDispatch();
  const { levels, selectedLevel, subjects, loading } = useAppSelector(
    (state) => state.parentChat
  );
  const activeSubject = useAppSelector((state) => state.chat.activeSubject);

  // Track initialization state
  const [isInitialized, setIsInitialized] = useState(false);

  // Track if we're currently fetching subjects to prevent duplicate calls
  const isFetchingSubjects = useRef(false);
  const isChangingSubject = useRef(false);

  // Memoize derived values to prevent unnecessary effect re-runs
  const memoizedLevels = useMemo(() => levels, [levels]);
  const memoizedSelectedLevel = useMemo(() => selectedLevel, [selectedLevel]);

  // Clear parent chat state when component unmounts or user logs out
  // const clearParentChatState = useCallback(() => {
  //   dispatch(setLevels([]));
  //   dispatch(setSelectedLevel(null));
  //   dispatch(setSubjects([]));
  //   dispatch(chatActions.setActiveSubject(''));
  //   dispatch(chatActions.setSessions([]));
  //   dispatch(chatActions.setMessages([]));
  //   isFetchingSubjects.current = false;
  //   isChangingSubject.current = false;
  // }, []);

  // Fetch messages for a session
  const fetchMessages = useCallback(
    async (sessionId: string) => {
      try {
        dispatch({ type: 'chat/setMessagesLoading', payload: true });
        console.log('from parent', sessionId);
        const response = await chatService.getAllMessages(sessionId);

        if (response.kind === ResponseKind.OK && response.data) {
          dispatch({ type: 'chat/setMessages', payload: response.data });
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        dispatch({ type: 'chat/setError', payload: 'Failed to load messages' });
      } finally {
        dispatch({ type: 'chat/setMessagesLoading', payload: false });
      }
    },
    [dispatch]
  );

  // Fetch sessions for a subject
  const fetchSessionsForSubject = useCallback(
    async (subjectName: string, levelId?: string): Promise<ChatSession[]> => {
      // Use the provided levelId or fall back to the current selectedLevel
      const currentLevel = levelId || selectedLevel;

      if (!currentLevel) {
        console.warn('Cannot fetch sessions: No level selected');
        return [];
      }

      if (!subjectName) {
        console.warn('Cannot fetch sessions: No subject provided');
        return [];
      }

      try {
        dispatch(chatActions.setSessionsLoading(true));
        const queryParams = new URLSearchParams();
        queryParams.append('subject', subjectName);
        queryParams.append('level_id', currentLevel);

        const queryString = queryParams.toString();
        const response = await chatService.getAllSessions(queryString);

        if (response.kind === ResponseKind.OK && response.data) {
          const sessions = response.data.chat_sessions || [];
          dispatch(chatActions.setSessions(sessions));
          if (sessions.length > 0) {
            dispatch(chatActions.setSelectedSession(sessions[0]));
            // Do NOT fetch messages here
          } else {
            dispatch(chatActions.setMessages([]));
            dispatch(chatActions.setSelectedSession(undefined));
          }
          return sessions;
        } else {
          throw new Error('Failed to fetch sessions');
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
        return [];
      } finally {
        dispatch(chatActions.setSessionsLoading(false));
      }
    },
    [dispatch, selectedLevel]
  );

  // Fetch all grade levels
  const fetchGradeLevels = useCallback(async () => {
    // Only fetch if we don't already have levels
    if (memoizedLevels.length > 0) {
      return memoizedLevels;
    }

    try {
      dispatch(setLoading(true));
      const response = await parentService.getParentGradeLevels();

      if (response.kind === 'ok' && response.data && response.data.length > 0) {
        const firstLevelId = response.data[0]?.id;

        // Set levels first
        dispatch(setLevels(response.data));

        // Then set selected level if needed
        if (!memoizedSelectedLevel && firstLevelId) {
          dispatch(setSelectedLevel(firstLevelId));
        }

        return response.data;
      } else {
        console.warn('No grade levels found');
        dispatch(setLevels([]));
        return [];
      }
    } catch (error) {
      console.error('Error fetching grade levels:', error);
      dispatch(setError('Failed to load grade levels'));
      return [];
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, memoizedLevels, memoizedSelectedLevel]);

  // Fetch subjects for the selected level
  const fetchSubjects = useCallback(
    async (levelId: string) => {
      if (!levelId) {
        return [];
      }

      // Reset the fetching flag if it was stuck
      if (isFetchingSubjects.current) {
        isFetchingSubjects.current = false;
      }

      isFetchingSubjects.current = true;

      try {
        dispatch(setLoading(true));
        const response = await parentService.getSubjectsByLevel(levelId);

        if (response.kind === 'ok' && response.data) {
          const subjectsData = response.data.map(
            (subject: any) =>
              ({
                id: subject.id,
                name: subject.name,
                code: subject.code,
                is_required: subject.is_required,
                levelId: levelId, // Track which level these subjects belong to
                level_id: levelId, // For backward compatibility
              } as Subject)
          );

          // Always dispatch setSubjects when we have data
          dispatch(setSubjects(subjectsData));

          return subjectsData;
        }
        return [];
      } catch (error) {
        console.error('Error fetching subjects:', error);
        dispatch(setError('Failed to load subjects'));
        return [];
      } finally {
        isFetchingSubjects.current = false;
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  // Handle level change
  const handleLevelChange = useCallback(
    async (levelId: string) => {
      if (!levelId) return;

      // Prevent multiple simultaneous level changes
      if (isFetchingSubjects.current) return;

      isFetchingSubjects.current = true;

      try {
        // Set loading state
        dispatch(chatActions.setSessionsLoading(true));

        // Clear previous state except messages and selectedSession
        dispatch(chatActions.setActiveSubject(''));
        dispatch(chatActions.setSessions([]));
        dispatch(setSubjects([])); // Clear subjects array
        // Do NOT clear messages or selectedSession here

        // Set the new level
        dispatch(setSelectedLevel(levelId));

        // Fetch subjects for the new level
        const subjects = await fetchSubjects(levelId);

        // If we have subjects, select the first one
        if (subjects?.length > 0) {
          const firstSubject = subjects[0].name;
          // Set the active subject first
          dispatch(chatActions.setActiveSubject(firstSubject));

          // Fetch sessions for the first subject
          const sessions = await fetchSessionsForSubject(firstSubject, levelId);

          // If we have sessions, select the first one
          if (sessions && sessions.length > 0) {
            const firstSession = sessions[0];
            // Set the selected session
            dispatch(chatActions.setSelectedSession(firstSession));
            // Do NOT fetch messages here
          } else {
            // If no sessions, clear the messages and selected session
            dispatch(chatActions.setMessages([]));
            dispatch(chatActions.setSelectedSession(undefined));
          }
        } else {
          // If no subjects, clear the messages and selected session
          dispatch(chatActions.setMessages([]));
          dispatch(chatActions.setSelectedSession(undefined));
        }
      } catch (error) {
        console.error('Error changing level:', error);
      } finally {
        // Reset flags and loading state
        dispatch(chatActions.setSessionsLoading(false));
        isFetchingSubjects.current = false;
      }
    },
    [dispatch, fetchSubjects, fetchSessionsForSubject]
  );

  // // Initialize chat data - run only once on mount
  // useEffect(() => {
  //   let isMounted = true;

  const initializeChat = async () => {
    // if (!isMounted) return;

    try {
      // Always fetch levels to ensure we have the latest data
      const loadedLevels = await fetchGradeLevels();
      !levels.length && dispatch(setLevels(loadedLevels));

      // if (!isMounted) return;

      if (loadedLevels?.length > 0) {
        // Always select the first level if none is selected
        const levelToSelect = memoizedSelectedLevel || loadedLevels[0]?.id;

        if (levelToSelect && !memoizedSelectedLevel) {
          // Force update the selected level and fetch its subjects
          dispatch(setSelectedLevel(levelToSelect));

          // Fetch subjects for the selected level
          const subjects = await fetchSubjects(levelToSelect);

          // Always select the first subject if we have subjects
          if (subjects?.length > 0) {
            const firstSubject = subjects[0].name;
            dispatch(chatActions.setActiveSubject(firstSubject));

            // Fetch sessions for the first subject
            await fetchSessionsForSubject(firstSubject, levelToSelect);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
    } finally {
      // if (isMounted) {
      setIsInitialized(true);
      // }
    }
  };

  //   initializeChat();

  //   return () => {
  //     isMounted = false;
  //   };
  //   // We only want this effect to run once on mount
  // }, []);

  // Handle subject change - this function is called when the user selects a subject
  const handleSubjectChange = useCallback(
    async (subjectName: string) => {
      if (!selectedLevel || isChangingSubject.current) {
        return;
      }

      isChangingSubject.current = true;

      try {
        // Set loading state
        dispatch(chatActions.setSessionsLoading(true));

        // Clear any existing sessions, but NOT messages or selectedSession
        dispatch(chatActions.setSessions([]));
        // Do NOT clear messages or selectedSession here

        // Set the new active subject
        dispatch(chatActions.setActiveSubject(subjectName));

        // Fetch sessions for the new subject and level
        const sessions = await fetchSessionsForSubject(
          subjectName,
          selectedLevel
        );

        // If we have sessions, select the first one
        if (sessions && sessions.length > 0) {
          // Set the selected session
          dispatch(chatActions.setSelectedSession(sessions[0]));
          // Do NOT fetch messages here
        } else {
          // If no sessions, clear the messages
          dispatch(chatActions.setMessages([]));
          dispatch(chatActions.setSelectedSession(undefined));
        }
      } catch (error) {
        console.error('Error changing subject:', error);
      } finally {
        dispatch(chatActions.setSessionsLoading(false));
        isChangingSubject.current = false;
      }
    },
    [dispatch, fetchSessionsForSubject, selectedLevel]
  );

  // Create a new session with the current level and subject
  const createNewSession = useCallback(
    async (sessionTitle: string = 'New Session') => {
      if (!selectedLevel || !activeSubject) {
        console.warn('Cannot create session: Missing level or subject');
        return null;
      }

      try {
        dispatch(chatActions.setIsCreatingNewSession(true));
        // Clear any existing messages to show the empty state
        dispatch(chatActions.setMessages([]));

        // Create a new session with the current subject and level
        const response = await chatService.createNewSession(
          sessionTitle,
          activeSubject,
          selectedLevel
        );

        if (response.kind === ResponseKind.OK && response.data) {
          // Clear existing sessions
          // dispatch(chatActions.setSessions([]));

          // Set the new session as selected
          dispatch(chatActions.setSelectedSession(response.data));

          // Clear messages to ensure empty state is shown
          // dispatch(chatActions.setMessages([]));

          // Refresh the sessions list and wait for it to complete
          await fetchSessionsForSubject(activeSubject);

          // Ensure the new session is selected after refresh
          dispatch(chatActions.setSelectedSession(response.data));

          return response.data;
        }
        return null;
      } catch (error) {
        console.error('Error creating session:', error);
        return null;
      } finally {
        dispatch(chatActions.setIsCreatingNewSession(false));
      }
    },
    [activeSubject, selectedLevel, dispatch, fetchSessionsForSubject]
  );

  // The handleSubjectChange function is defined above with better error handling
  // and isChangingSubject flag to prevent race conditions

  // Get sessions and selectedSession from Redux store
  const { sessions, selectedSession } = useAppSelector((state) => state.chat);

  // Effect to handle session selection when sessions change
  useEffect(() => {
    if (sessions?.length > 0 && !selectedSession) {
      // Select the first session when sessions are loaded and no session is selected
      dispatch(chatActions.setSelectedSession(sessions[0]));
    }
  }, [sessions, selectedSession, dispatch]);

  // Clear state when component unmounts
  // useEffect(() => {
  //   return () => {
  //     clearParentChatState();
  //   };
  // }, []);

  return {
    levels,
    selectedLevel,
    subjects,
    loading,
    activeSubject,
    fetchGradeLevels,
    fetchSubjects,
    handleLevelChange,
    handleSubjectChange,
    fetchSessionsForSubject,
    fetchMessages,
    createNewSession,
    initializeChat,
  };
};

export default useParentChat;
